// ============================================================
//  src/dashboard-profile-card.js — Tarjeta resumen de perfil
// ============================================================

import { getPerfilActual, setPerfilActual } from './perfil.js';
import { auth } from './firebase.js';
import { mostrarToast } from './ui.js';
import { renderizarTarjetaHTML, MODO_TARJETA_COMPLETO } from './shared/card-template.js';
import { guardarPerfil } from './perfil-datos.js';
import { actualizarPerfilPublico } from './public-profile.js';
import { t, idiomaActual } from './i18n.js';
import { searchDxcc, getDxccByLabel } from './utils/dxcc-utils.js';
import { aplicarPrefijoCampoTelefono } from './data/dial-codes.js';
import { initInfoTooltip } from './utils/info-tooltip.js';

// ============================================================
//  Utilidades
// ============================================================

function normalizarTexto(valor) {
    return String(valor ?? '').trim();
}

function normalizeGrid(raw) {
    const s = String(raw ?? '').trim();
    if (s.length !== 6) return '';
    const n = s.slice(0, 2).toUpperCase() + s.slice(2, 4) + s.slice(4, 6).toLowerCase();
    return /^[A-Z]{2}[0-9]{2}[a-z]{2}$/.test(n) ? n : '';
}

function formatearIndicativosEspeciales(indicativosEspeciales) {
    if (!Array.isArray(indicativosEspeciales) || !indicativosEspeciales.length) {
        return [];
    }

    return indicativosEspeciales
        .map(x => normalizarTexto(x).toUpperCase())
        .filter(Boolean);
}

function obtenerAvatar(perfil) {
    const avatar = normalizarTexto(perfil?.avatar);
    if (avatar) return avatar;

    return normalizarTexto(auth?.currentUser?.photoURL);
}

function obtenerNombreVisible(perfil) {
    return (
        normalizarTexto(perfil?.nombreCompleto) ||
        normalizarTexto(perfil?.nombre) ||
        normalizarTexto(auth?.currentUser?.displayName) ||
        'Usuario'
    );
}

function obtenerEmailVisible(perfil) {
    return (
        normalizarTexto(perfil?.correoElectronico) ||
        normalizarTexto(perfil?.email) ||
        normalizarTexto(auth?.currentUser?.email) ||
        ''
    );
}

function obtenerPaisEntidad(perfil) {
    const pais = normalizarTexto(perfil?.pais);
    const dxcc = normalizarTexto(perfil?.dxcc);

    if (pais && dxcc) return `${pais} (${dxcc})`;
    if (pais) return pais;
    return '';
}

function extraerPuesto(p) {
    const valor = Number(p);
    return Number.isFinite(valor) && valor > 0 ? valor : null;
}

function formatearIndicativoPrincipal(perfil) {
    return normalizarTexto(perfil?.indicativoOperador).toUpperCase();
}

function obtenerBandasDisponibles(ediciones) {
    const mapa = contarPorClave(ediciones, e => e?.banda);
    return obtenerFrecuenciaMayor(mapa);
}

function obtenerModosDisponibles(ediciones) {
    const mapa = contarPorClave(ediciones, e => e?.modo);
    return obtenerFrecuenciaMayor(mapa);
}

function obtenerConcursoMasTrabajado(ediciones) {
    const mapa = contarPorClave(ediciones, e => e?.concurso);
    return obtenerFrecuenciaMayor(mapa);
}

function contarPorClave(ediciones, selector) {
    const mapa = new Map();

    for (const edicion of ediciones) {
        const clave = normalizarTexto(selector(edicion));
        if (!clave) continue;
        mapa.set(clave, (mapa.get(clave) || 0) + 1);
    }

    return mapa;
}

function obtenerFrecuenciaMayor(mapaConteos) {
    let ganador = null;
    let max = 0;

    for (const [clave, cantidad] of mapaConteos.entries()) {
        if (cantidad > max) {
            ganador = clave;
            max = cantidad;
        }
    }

    return ganador
        ? { valor: ganador, cantidad: max }
        : { valor: '—', cantidad: 0 };
}

function obtenerUltimoConcurso(ediciones) {
    if (!Array.isArray(ediciones) || !ediciones.length) return '—';

    const copia = [...ediciones];

    copia.sort((a, b) => {
        const fechaA = a?.fechaFin instanceof Date ? a.fechaFin.getTime() : 0;
        const fechaB = b?.fechaFin instanceof Date ? b.fechaFin.getTime() : 0;
        return fechaB - fechaA;
    });

    const ultimo = copia[0];
    return normalizarTexto(ultimo?.concurso) || '—';
}

function obtenerClavePuestoConConcursos(ediciones, nivel) {
    let mejor = null;
    let ambitoMejor = '';
    let concursos = [];

    for (const edicion of ediciones) {
        const puesto = extraerPuesto(edicion?.posicion?.[nivel]?.puesto);
        if (puesto == null) continue;

        const nombreConcurso = normalizarTexto(edicion?.concurso);
        if (!nombreConcurso) continue;

        if (mejor == null || puesto < mejor) {
            mejor = puesto;
            ambitoMejor = normalizarTexto(edicion?.posicion?.[nivel]?.ambito);
            concursos = [nombreConcurso];
            continue;
        }

        if (puesto === mejor) {
            concursos.push(nombreConcurso);
        }
    }

    const unicos = [...new Set(concursos)];

    return {
        puesto: mejor,
        ambito: ambitoMejor,
        concursos: unicos
    };
}

// ============================================================
//  Cálculo de resumen
// ============================================================

export function calcularResumenPerfil(ediciones = [], perfil = {}) {
    const lista = Array.isArray(ediciones) ? ediciones : [];

    const concursosUnicos = new Set(
        lista
            .map(e => normalizarTexto(e?.concurso))
            .filter(Boolean)
    ).size;

    const modoMasUsado = obtenerModosDisponibles(lista);
    const bandaMasUsada = obtenerBandasDisponibles(lista);
    const concursoMasTrabajado = obtenerConcursoMasTrabajado(lista);

    return {
        identidad: {
            avatar: obtenerAvatar(perfil),
            nombreVisible: obtenerNombreVisible(perfil),
            indicativoPrincipal: formatearIndicativoPrincipal(perfil),
            indicativosEspeciales: formatearIndicativosEspeciales(perfil?.indicativosEspeciales),
            club: normalizarTexto(perfil?.club),
            paisEntidad: obtenerPaisEntidad(perfil),
            qth: normalizarTexto(perfil?.qth),
            zonaCQ: normalizarTexto(perfil?.zonaCQ),
            zonaITU: normalizarTexto(perfil?.zonaITU),
            gridLocator: normalizeGrid(perfil?.gridLocator),
            telefono: normalizarTexto(perfil?.telefono),
            correoElectronico: obtenerEmailVisible(perfil),
            qrzUrl: normalizarTexto(perfil?.qrzCallsign)
                ? `https://www.qrz.com/db/${normalizarTexto(perfil.qrzCallsign).toUpperCase()}`
                : '',
            clubLogUrl: perfil?.clubLogCallsign
                ? `https://clublog.org/logsearch/${normalizarTexto(perfil.clubLogCallsign).toUpperCase()}`
                : '',
            eQslUrl: perfil?.eQslCallsign
                ? `https://www.eqsl.cc/Member.cfm?CALLSIGN=${normalizarTexto(perfil.eQslCallsign).toUpperCase()}`
                : '',
            redes: Array.isArray(perfil?.redesSociales) ? perfil.redesSociales : []
        },

        stats: {
            concursos: concursosUnicos,
            participaciones: lista.length,
            mejorPais: obtenerClavePuestoConConcursos(lista, 'local'),
            mejorContinente: obtenerClavePuestoConConcursos(lista, 'regional'),
            mejorMundo: obtenerClavePuestoConConcursos(lista, 'mundial'),
            modoMasUsado: modoMasUsado.valor,
            modoMasUsadoCantidad: modoMasUsado.cantidad,
            bandaMasUsada: bandaMasUsada.valor,
            bandaMasUsadaCantidad: bandaMasUsada.cantidad,
            concursoMasTrabajado: concursoMasTrabajado.valor,
            concursoMasTrabajadoCantidad: concursoMasTrabajado.cantidad,
            ultimoConcurso: obtenerUltimoConcurso(lista)
        }
    };
}

// ============================================================
//  Edición inline columna izquierda
// ============================================================

let _avatarPendiente = null;

// Mapeo fieldKey (data-field-key del template) → campo del perfil
const _CAMPOS_META = [
    { key: 'club',      field: 'club' },
    { key: 'pais',      field: 'pais' },
    { key: 'zona-cq',   field: 'zonaCQ' },
    { key: 'zona-itu',  field: 'zonaITU' },
    { key: 'qth',       field: 'qth' },
    { key: 'grid',      field: 'gridLocator' },
    { key: 'telefono',  field: 'telefono' },
    { key: 'correo',    field: 'correoElectronico' },
];

function _getLabels() {
    return {
        club:                 t('tarjeta.meta.club'),
        paisDxcc:             t('tarjeta.meta.pais'),
        zonaCQ:               t('tarjeta.meta.zona-cq'),
        zonaITU:              t('tarjeta.meta.zona-itu'),
        qth:                  t('tarjeta.meta.qth'),
        grid:                 t('tarjeta.meta.grid'),
        telefono:             t('tarjeta.meta.telefono'),
        correo:               t('tarjeta.meta.correo'),
        enlaces:              t('tarjeta.meta.enlaces'),
        statsTitle:           t('tarjeta.stats.titulo'),
        statsSubtitle:        t('tarjeta.stats.subtitulo'),
        concursos:            t('tarjeta.stats.concursos'),
        participaciones:      t('tarjeta.stats.participaciones'),
        mejoresPos:           t('tarjeta.stats.mejores'),
        mejorPais:            t('tarjeta.stats.mejor-pais'),
        mejorContinente:      t('tarjeta.stats.mejor-continente'),
        mejorMundo:           t('tarjeta.stats.mejor-mundo'),
        totalConcursoUno:     t('tarjeta.stats.total-uno'),
        totalConcursoVarios:  t('tarjeta.stats.total-varios'),
        verMasUno:            t('tarjeta.stats.ver-mas-uno'),
        verMasVarios:         t('tarjeta.stats.ver-mas-varios'),
        mostrarMenos:         t('tarjeta.stats.mostrar-menos'),
        modoMasUsado:         t('tarjeta.footer.modo'),
        concursoMasTrabajado: t('tarjeta.footer.concurso'),
        ultimoConcurso:       t('tarjeta.footer.ultimo'),
    };
}

function _inyectarBotonEditar(leftCol, ediciones) {
    const btn = document.createElement('button');
    btn.className = 'btn-editar-tarjeta';
    btn.title = 'Editar';
    btn.setAttribute('aria-label', t('tarjeta.btn.editar-perfil'));
    btn.dataset.i18nAria = 'tarjeta.btn.editar-perfil';
    btn.innerHTML = '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M11.5 2.5l2 2L5 13H3v-2L11.5 2.5z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M9.5 4.5l2 2" stroke="currentColor" stroke-width="1.3"/></svg>';
    btn.addEventListener('click', () => _activarModoEdicion(leftCol, ediciones));
    leftCol.appendChild(btn);
}

function _mostrarCropAvatar(file, onConfirm) {
    const CROP_SIZE = 240;
    const OUTPUT_SIZE = 256;

    const reader = new FileReader();
    reader.onload = e => {
        const img = new Image();
        img.onload = () => {
            const minScale = Math.max(CROP_SIZE / img.naturalWidth, CROP_SIZE / img.naturalHeight);
            let scale = minScale;
            let dx = (CROP_SIZE - img.naturalWidth * scale) / 2;
            let dy = (CROP_SIZE - img.naturalHeight * scale) / 2;
            let isDragging = false;
            let startX, startY, startDx, startDy;

            const backdrop = document.createElement('div');
            backdrop.className = 'tarjeta-crop-overlay';

            const panel = document.createElement('div');
            panel.className = 'tarjeta-crop-panel';

            const titulo = document.createElement('p');
            titulo.className = 'tarjeta-crop-titulo';
            titulo.textContent = t('tarjeta.crop.titulo');

            const area = document.createElement('div');
            area.className = 'tarjeta-crop-area';

            const imgEl = document.createElement('img');
            imgEl.src = e.target.result;
            imgEl.className = 'tarjeta-crop-img';
            imgEl.draggable = false;
            area.appendChild(imgEl);

            const zoomRow = document.createElement('div');
            zoomRow.className = 'tarjeta-crop-controles';

            const zoomLabel = document.createElement('span');
            zoomLabel.className = 'tarjeta-crop-zoom-label';
            zoomLabel.textContent = '−';

            const slider = document.createElement('input');
            slider.type = 'range';
            slider.className = 'tarjeta-crop-zoom';
            slider.min = String(minScale);
            slider.max = String(minScale * 4);
            slider.step = '0.01';
            slider.value = String(minScale);

            const zoomLabelMax = document.createElement('span');
            zoomLabelMax.className = 'tarjeta-crop-zoom-label';
            zoomLabelMax.textContent = '+';

            zoomRow.append(zoomLabel, slider, zoomLabelMax);

            const acciones = document.createElement('div');
            acciones.className = 'tarjeta-crop-acciones';

            const btnCancelar = document.createElement('button');
            btnCancelar.type = 'button';
            btnCancelar.className = 'btn-cancelar tarjeta-crop-btn';
            btnCancelar.textContent = t('common.cancelar');

            const btnConfirmar = document.createElement('button');
            btnConfirmar.type = 'button';
            btnConfirmar.className = 'btn-guardar tarjeta-crop-btn';
            btnConfirmar.textContent = t('tarjeta.crop.confirmar');

            acciones.append(btnCancelar, btnConfirmar);
            panel.append(titulo, area, zoomRow, acciones);
            backdrop.appendChild(panel);

            function _clamp() {
                const w = img.naturalWidth * scale;
                const h = img.naturalHeight * scale;
                dx = Math.min(0, Math.max(CROP_SIZE - w, dx));
                dy = Math.min(0, Math.max(CROP_SIZE - h, dy));
            }

            function _aplicar() {
                imgEl.style.width = `${img.naturalWidth * scale}px`;
                imgEl.style.height = `${img.naturalHeight * scale}px`;
                imgEl.style.left = `${dx}px`;
                imgEl.style.top = `${dy}px`;
            }

            _aplicar();

            function _onMove(cx, cy) {
                dx = startDx + (cx - startX);
                dy = startDy + (cy - startY);
                _clamp();
                _aplicar();
            }

            area.addEventListener('mousedown', ev => {
                isDragging = true;
                startX = ev.clientX;
                startY = ev.clientY;
                startDx = dx;
                startDy = dy;
                ev.preventDefault();
            });

            function _mouseMove(ev) {
                if (isDragging) _onMove(ev.clientX, ev.clientY);
            }
            function _mouseUp() {
                isDragging = false;
            }

            document.addEventListener('mousemove', _mouseMove);
            document.addEventListener('mouseup', _mouseUp);

            area.addEventListener('touchstart', ev => {
                const touch = ev.touches[0];
                isDragging = true;
                startX = touch.clientX;
                startY = touch.clientY;
                startDx = dx;
                startDy = dy;
                ev.preventDefault();
            }, { passive: false });

            function _touchMove(ev) {
                if (!isDragging) return;
                const touch = ev.touches[0];
                _onMove(touch.clientX, touch.clientY);
                ev.preventDefault();
            }
            function _touchEnd() {
                isDragging = false;
            }

            document.addEventListener('touchmove', _touchMove, { passive: false });
            document.addEventListener('touchend', _touchEnd);

            slider.addEventListener('input', () => {
                const newScale = parseFloat(slider.value);
                const cx = CROP_SIZE / 2;
                const cy = CROP_SIZE / 2;
                const imgCx = (cx - dx) / scale;
                const imgCy = (cy - dy) / scale;
                scale = newScale;
                dx = cx - imgCx * scale;
                dy = cy - imgCy * scale;
                _clamp();
                _aplicar();
            });

            function _cleanup() {
                document.removeEventListener('mousemove', _mouseMove);
                document.removeEventListener('mouseup', _mouseUp);
                document.removeEventListener('touchmove', _touchMove);
                document.removeEventListener('touchend', _touchEnd);
                backdrop.remove();
            }

            btnCancelar.addEventListener('click', _cleanup);

            btnConfirmar.addEventListener('click', () => {
                const canvas = document.createElement('canvas');
                canvas.width = OUTPUT_SIZE;
                canvas.height = OUTPUT_SIZE;
                const srcX = -dx / scale;
                const srcY = -dy / scale;
                const srcW = CROP_SIZE / scale;
                const srcH = CROP_SIZE / scale;
                canvas.getContext('2d').drawImage(img, srcX, srcY, srcW, srcH, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
                _cleanup();
                onConfirm(canvas.toDataURL('image/jpeg', 0.85));
            });

            document.body.appendChild(backdrop);
        };
        img.src = e.target.result;
    };
    reader.readAsDataURL(file);
}

function _editarAvatar(leftCol) {
    const wrap = leftCol.querySelector('.profile-summary-card__avatar-wrap');
    if (!wrap) return;

    const avatarEl = wrap.querySelector('.profile-summary-card__avatar');
    wrap.classList.add('tarjeta-edit-avatar-wrap');

    const camOverlay = document.createElement('div');
    camOverlay.className = 'tarjeta-edit-avatar-overlay';
    camOverlay.innerHTML = '<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 15V6a1 1 0 011-1h2.5l1.5-2h4l1.5 2H16a1 1 0 011 1v9a1 1 0 01-1 1H4a1 1 0 01-1-1z" stroke="currentColor" stroke-width="1.3"/><circle cx="10" cy="10.5" r="2.5" stroke="currentColor" stroke-width="1.3"/></svg>';

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.style.display = 'none';

    wrap.appendChild(camOverlay);
    wrap.appendChild(fileInput);
    wrap.style.cursor = 'pointer';
    wrap.addEventListener('click', () => fileInput.click());

    fileInput.addEventListener('change', (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        fileInput.value = '';
        _mostrarCropAvatar(file, (b64) => {
            _avatarPendiente = b64;
            if (avatarEl?.tagName === 'IMG') {
                avatarEl.src = b64;
            } else if (avatarEl) {
                const newImg = document.createElement('img');
                newImg.className = avatarEl.className.replace('--fallback', '');
                newImg.src = b64;
                newImg.alt = 'Avatar';
                avatarEl.replaceWith(newImg);
            }
        });
    });
}

function _editarIdentidad(leftCol, perfil) {
    const nameEl = leftCol.querySelector('.profile-summary-card__name');
    if (nameEl) {
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'tarjeta-edit-input tarjeta-edit-input--nombre';
        input.value = perfil.nombreCompleto || perfil.nombre || auth.currentUser?.displayName || '';
        input.dataset.field = 'nombreCompleto';
        nameEl.replaceChildren(input);
    }

    const callsignsEl = leftCol.querySelector('.profile-summary-card__callsigns');
    if (callsignsEl) {
        const inputPrincipal = document.createElement('input');
        inputPrincipal.type = 'text';
        inputPrincipal.className = 'tarjeta-edit-input tarjeta-edit-input--callsign';
        inputPrincipal.value = perfil.indicativoOperador || '';
        inputPrincipal.placeholder = t('tarjeta.placeholder.indicativo-op');
        inputPrincipal.dataset.field = 'indicativoOperador';

        const inputEspeciales = document.createElement('input');
        inputEspeciales.type = 'text';
        inputEspeciales.className = 'tarjeta-edit-input tarjeta-edit-input--callsign';
        const especiales = Array.isArray(perfil.indicativosEspeciales)
            ? perfil.indicativosEspeciales.join(', ')
            : (perfil.indicativoEspecial || '');
        inputEspeciales.value = especiales;
        inputEspeciales.placeholder = t('tarjeta.placeholder.indicativos-esp');
        inputEspeciales.dataset.field = 'indicativosEspeciales';
        inputEspeciales.dataset.fieldType = 'array';

        const hint = document.createElement('p');
        hint.className = 'tarjeta-edit-hint';
        hint.textContent = t('tarjeta.hint.indicativos-esp');

        callsignsEl.replaceChildren(inputPrincipal, inputEspeciales, hint);
    }
}

function _editarMetaItems(leftCol, perfil) {
    leftCol.querySelectorAll('.profile-summary-card__meta-item').forEach(item => {
        const fieldKey = item.dataset.fieldKey;
        const valueEl = item.querySelector('.profile-summary-card__meta-value');
        if (!fieldKey || !valueEl) return;

                if (fieldKey === 'enlaces') {
            function obtenerIndicativosEditables() {
                const inputPrincipal = leftCol.querySelector('[data-field="indicativoOperador"]');
                const inputEspeciales = leftCol.querySelector('[data-field="indicativosEspeciales"]');

                const principalActual = inputPrincipal?.value?.trim() || perfil.indicativoOperador || '';

                const especialesActuales = inputEspeciales?.value?.trim()
                    || (Array.isArray(perfil.indicativosEspeciales)
                        ? perfil.indicativosEspeciales.join(', ')
                        : (perfil.indicativoEspecial || ''));

                const lista = [
                    principalActual,
                    ...String(especialesActuales)
                        .split(',')
                        .map(c => c.trim())
                ]
                    .filter(Boolean)
                    .map(c => c.toUpperCase());

                return [...new Set(lista)];
            }

            function poblarSelect(sel, sinClave, valorActual = '') {
                const valorPrevio = (sel.value || valorActual || '').toUpperCase();
                const opciones = obtenerIndicativosEditables();

                sel.innerHTML = '';

                const optVacia = document.createElement('option');
                optVacia.value = '';
                optVacia.textContent = t(sinClave);
                sel.appendChild(optVacia);

                opciones.forEach(c => {
                    const opt = document.createElement('option');
                    opt.value = c;
                    opt.textContent = c;
                    if (c === valorPrevio) opt.selected = true;
                    sel.appendChild(opt);
                });
            }

            function _crearSelect(field, sinClave, valorActual) {
                const sel = document.createElement('select');
                sel.className = 'tarjeta-edit-input tarjeta-edit-select';
                sel.dataset.field = field;
                sel.dataset.sinClave = sinClave;

                poblarSelect(sel, sinClave, valorActual);

                return sel;
            }

            const selectQrz = _crearSelect('qrzCallsign', 'tarjeta.select.sin-qrz', perfil.qrzCallsign);
            const selectCl = _crearSelect('clubLogCallsign', 'tarjeta.select.sin-clublog', perfil.clubLogCallsign);
            const selectEq = _crearSelect('eQslCallsign', 'tarjeta.select.sin-eqsl', perfil.eQslCallsign);

            function actualizarSelectsEnlaces() {
                poblarSelect(selectQrz, 'tarjeta.select.sin-qrz');
                poblarSelect(selectCl, 'tarjeta.select.sin-clublog');
                poblarSelect(selectEq, 'tarjeta.select.sin-eqsl');
            }

            const inputPrincipal = leftCol.querySelector('[data-field="indicativoOperador"]');
            const inputEspeciales = leftCol.querySelector('[data-field="indicativosEspeciales"]');

            inputPrincipal?.addEventListener('input', actualizarSelectsEnlaces);
            inputEspeciales?.addEventListener('input', actualizarSelectsEnlaces);

            valueEl.replaceChildren(selectQrz, selectCl, selectEq);
            return;
        }

        if (fieldKey === 'pais') {
            const currentPais = normalizarTexto(perfil.pais || '');
            const currentDxcc = normalizarTexto(String(perfil.dxcc || ''));
            const currentLabel = currentPais && currentDxcc
                ? `${currentPais} (${currentDxcc})`
                : currentPais;

            const selectorWrap = document.createElement('div');
            selectorWrap.className = 'dxcc-selector';
            selectorWrap.style.position = 'relative';
            selectorWrap.style.width = '100%';

            const searchInput = document.createElement('input');
            searchInput.type = 'text';
            searchInput.className = 'tarjeta-edit-input';
            searchInput.setAttribute('autocomplete', 'off');
            searchInput.placeholder = 'Buscar entidad DXCC...';
            searchInput.value = currentLabel;

            const dropdown = document.createElement('div');
            dropdown.className = 'dxcc-dropdown';
            dropdown.style.position = 'absolute';
            dropdown.style.top = 'calc(100% + 4px)';
            dropdown.style.left = '0';
            dropdown.style.right = '0';
            dropdown.style.display = 'none';
            dropdown.style.maxHeight = '220px';
            dropdown.style.overflowY = 'auto';
            dropdown.style.background = 'var(--color-superficie)';
            dropdown.style.border = '1px solid var(--color-borde)';
            dropdown.style.borderRadius = '8px';
            dropdown.style.boxShadow = '0 10px 24px rgba(0,0,0,0.18)';
            dropdown.style.zIndex = '50';

            const hiddenPais = document.createElement('input');
            hiddenPais.type = 'hidden';
            hiddenPais.dataset.field = 'pais';
            hiddenPais.value = currentPais;

            const hiddenDxcc = document.createElement('input');
            hiddenDxcc.type = 'hidden';
            hiddenDxcc.dataset.field = 'dxcc';
            hiddenDxcc.value = currentDxcc;

            function actualizarZonas(entidad) {
                const cqInput = leftCol.querySelector('[data-field="zonaCQ"]');
                const ituInput = leftCol.querySelector('[data-field="zonaITU"]');

                if (cqInput) cqInput.value = entidad ? String(entidad.cq ?? '') : '';
                if (ituInput) ituInput.value = entidad ? String(entidad.itu ?? '') : '';
            }

            function limpiarSeleccionDxcc() {
                hiddenPais.value = '';
                hiddenDxcc.value = '';
                actualizarZonas(null);
            }

            function ocultarDropdown() {
                dropdown.innerHTML = '';
                dropdown.style.display = 'none';
            }

            function renderOpciones(filtro = '') {
                const resultados = searchDxcc(filtro);

                dropdown.innerHTML = '';

                if (!resultados.length) {
                    dropdown.style.display = 'none';
                    return;
                }

                resultados.forEach(entidad => {
                    const opt = document.createElement('button');
                    opt.type = 'button';
                    opt.className = 'dxcc-option';
                    opt.textContent = `${entidad.name} (${entidad.dxcc})`;
                    opt.style.display = 'block';
                    opt.style.width = '100%';
                    opt.style.padding = '10px 12px';
                    opt.style.textAlign = 'left';
                    opt.style.border = '0';
                    opt.style.background = 'transparent';
                    opt.style.color = 'var(--color-texto)';
                    opt.style.cursor = 'pointer';
                    opt.style.font = 'inherit';

                    opt.addEventListener('mouseenter', () => {
                        opt.style.background = 'var(--color-superficie-alt)';
                    });
                    opt.addEventListener('mouseleave', () => {
                        opt.style.background = 'transparent';
                    });

                    opt.addEventListener('mousedown', (ev) => {
                        ev.preventDefault();
                        searchInput.value = `${entidad.name} (${entidad.dxcc})`;
                        hiddenPais.value = entidad.name;
                        hiddenDxcc.value = String(entidad.dxcc);
                        actualizarZonas(entidad);
                        aplicarPrefijoCampoTelefono(leftCol.querySelector('[data-field="telefono"]'), entidad.name);
                        ocultarDropdown();
                        searchInput.dispatchEvent(new Event('input', { bubbles: true }));
                        searchInput.dispatchEvent(new Event('change', { bubbles: true }));
                    });

                    dropdown.appendChild(opt);
                });

                dropdown.style.display = 'block';
            }

            searchInput.addEventListener('focus', () => {
                renderOpciones(searchInput.value);
            });

            searchInput.addEventListener('input', () => {
                const val = searchInput.value.trim();

                if (!val) {
                    limpiarSeleccionDxcc();
                    ocultarDropdown();
                    return;
                }

                const found = getDxccByLabel(val);

                if (found) {
                    hiddenPais.value = found.name;
                    hiddenDxcc.value = String(found.dxcc);
                    actualizarZonas(found);
                    aplicarPrefijoCampoTelefono(leftCol.querySelector('[data-field="telefono"]'), found.name);
                } else {
                    limpiarSeleccionDxcc();
                }

                renderOpciones(val);
            });

            searchInput.addEventListener('blur', () => {
                setTimeout(() => {
                    if (!selectorWrap.contains(document.activeElement)) {
                        ocultarDropdown();
                    }
                }, 150);
            });

            selectorWrap.appendChild(searchInput);
            selectorWrap.appendChild(dropdown);

            valueEl.replaceChildren(selectorWrap, hiddenPais, hiddenDxcc);
            return;
        }

        const campo = _CAMPOS_META.find(c => c.key === fieldKey);
        if (!campo) return;

        const raw = String(perfil[campo.field] ?? '');
        const input = document.createElement('input');
        input.type = campo.field === 'correoElectronico' ? 'email' : 'text';
        input.className = 'tarjeta-edit-input';
        input.value = raw;
        input.dataset.field = campo.field;

        valueEl.replaceChildren(input);
    });
}

function _activarModoEdicion(leftCol, ediciones) {
    _avatarPendiente = null;
    const perfil = getPerfilActual() || {};

    _editarAvatar(leftCol);
    _editarIdentidad(leftCol, perfil);
    _editarMetaItems(leftCol, perfil);

    // Checkboxes de visibilidad pública — directamente bajo teléfono/correo
    const correoItem = leftCol.querySelector('[data-field-key="correo"]');
    if (correoItem) {
        const chkWrap = document.createElement('div');
        chkWrap.className = 'tarjeta-visib-checks';

        [
            { fieldBool: 'mostrarTelefonoPublico', i18nKey: 'perfil.mostrar-telefono', valor: perfil.mostrarTelefonoPublico },
            { fieldBool: 'mostrarEmailPublico',    i18nKey: 'perfil.mostrar-correo',   valor: perfil.mostrarEmailPublico },
        ].forEach(({ fieldBool, i18nKey, valor }) => {
            const lbl = document.createElement('label');
            lbl.className = 'pref-row';

            const span = document.createElement('span');
            span.className = 'pref-row__label';
            span.textContent = t(i18nKey);

            const ctrl = document.createElement('span');
            ctrl.className = 'pref-switch-ctrl';

            const chk = document.createElement('input');
            chk.type = 'checkbox';
            chk.className = 'pref-switch__input';
            chk.dataset.fieldBool = fieldBool;
            chk.checked = valor !== false;

            const track = document.createElement('span');
            track.className = 'pref-switch__track';

            ctrl.append(chk, track);
            lbl.append(span, ctrl);
            chkWrap.appendChild(lbl);
        });

        correoItem.insertAdjacentElement('afterend', chkWrap);
    }

    leftCol.querySelectorAll('a').forEach(a => {
        a.dataset.editHrefBak = a.getAttribute('href') || '';
        a.removeAttribute('href');
        a.classList.add('tarjeta-edit-link-disabled');
    });

    leftCol.querySelector('.btn-editar-tarjeta')?.remove();

    const bar = document.createElement('div');
    bar.className = 'tarjeta-edit-bar';

    const btnCancelar = document.createElement('button');
    btnCancelar.type = 'button';
    btnCancelar.className = 'btn-cancelar tarjeta-edit-bar__btn';
    btnCancelar.textContent = t('common.cancelar');
    btnCancelar.addEventListener('click', () => {
        _avatarPendiente = null;
        renderizarTarjetaResumenPerfil(ediciones);
    });

    const btnGuardar = document.createElement('button');
    btnGuardar.type = 'button';
    btnGuardar.className = 'btn-guardar tarjeta-edit-bar__btn';
    btnGuardar.textContent = t('common.guardar');
    btnGuardar.addEventListener('click', () => _guardarCambiosTarjeta(leftCol, ediciones, btnGuardar));

    bar.append(btnCancelar, btnGuardar);
    leftCol.appendChild(bar);
}

async function _guardarCambiosTarjeta(leftCol, ediciones, btnGuardar) {
    const usuario = auth.currentUser;
    if (!usuario) return;

    const perfil = getPerfilActual() || {};
    const updates = {};

    if (_avatarPendiente) updates.avatar = _avatarPendiente;

    leftCol.querySelectorAll('[data-field]').forEach(el => {
        const field = el.dataset.field;
        if (!field) return;
        const raw = (el.value || '').trim();

        if (el.dataset.fieldType === 'array') {
            updates[field] = raw.split(',').map(x => x.trim().toUpperCase()).filter(Boolean);
        } else if (field === 'gridLocator') {
            updates[field] = normalizeGrid(raw);
        } else if (['indicativoOperador', 'qrzCallsign', 'clubLogCallsign', 'eQslCallsign'].includes(field)) {
            updates[field] = raw.toUpperCase();
        } else {
            updates[field] = raw;
        }
    });

    leftCol.querySelectorAll('[data-field-bool]').forEach(el => {
        updates[el.dataset.fieldBool] = el.checked;
    });

    if (updates.nombreCompleto !== undefined) updates.nombre = updates.nombreCompleto;

    btnGuardar.disabled = true;
    btnGuardar.textContent = t('common.guardando');

    try {
        await guardarPerfil(usuario.uid, { ...updates, ultimoAcceso: new Date() });
        _avatarPendiente = null;
        const perfilActualizado = { ...perfil, ...updates };
        setPerfilActual(perfilActualizado);
        actualizarPerfilPublico(usuario.uid, perfilActualizado, ediciones, perfil.indicativoOperador || null).catch(console.error);

        if (updates.indicativoOperador) {
            const elIndicativo = document.getElementById('navbar-indicativo');
            if (elIndicativo) elIndicativo.textContent = updates.indicativoOperador;
        }

        mostrarToast(t('toast.perfil.guardado'), 'exito');
        renderizarTarjetaResumenPerfil(ediciones);
    } catch (err) {
        console.error('[Tarjeta] Error al guardar:', err);
        mostrarToast(t('toast.perfil.error'), 'error');
        btnGuardar.disabled = false;
        btnGuardar.textContent = t('common.guardar');
    }
}

// ============================================================
//  Render
// ============================================================

export function renderizarTarjetaConResumen(contenedor, resumen, labels) {
    contenedor.innerHTML = renderizarTarjetaHTML(resumen, labels, { modo: MODO_TARJETA_COMPLETO });
}

export function renderizarTarjetaResumenPerfil(ediciones = []) {
    const contenedor = document.getElementById('dashboard-profile-card-container');
    if (!contenedor) return;

    const perfil = getPerfilActual() || {};
    const resumen = calcularResumenPerfil(ediciones, perfil);
    renderizarTarjetaConResumen(contenedor, resumen, _getLabels());

    // Inyectar botón de edición en columna izquierda
    const leftCol = contenedor.querySelector('.profile-summary-card__left');
    if (leftCol) _inyectarBotonEditar(leftCol, ediciones);

    const callsign = normalizarTexto(perfil.indicativoOperador).toUpperCase();
    if (!callsign) return;

    // ── Sección "Vista pública" — barra compacta ─────────────
    const LS_LANG  = 'publicCardLang';
    const LS_THEME = 'publicCardTheme';

    let cardLang  = localStorage.getItem(LS_LANG)  || idiomaActual() || 'es';
    let cardTheme = localStorage.getItem(LS_THEME) || (localStorage.getItem('theme') === 'light' ? 'light' : 'dark');

    function _urlPublica() {
        return `https://app.contestmanager.uy/u/${encodeURIComponent(callsign)}?theme=${cardTheme}&lang=${cardLang}`;
    }
    function _urlEmbed() {
        return `https://app.contestmanager.uy/embed-live/${encodeURIComponent(callsign)}?theme=${cardTheme}&lang=${cardLang}`;
    }

    // — Botón "Ver tarjeta pública"
    const btnVer = document.createElement('a');
    btnVer.className = 'vp-btn';
    btnVer.href = _urlPublica();
    btnVer.target = '_blank';
    btnVer.rel = 'noopener noreferrer';
    btnVer.dataset.i18n = 'tarjeta.vista-publica.ver';
    btnVer.textContent = t('tarjeta.vista-publica.ver');

    // — Info ⓘ + botón "Copiar tarjeta para QRZ"
    const infoWrapper = document.createElement('div');
    infoWrapper.className = 'iframe-info-wrapper';
    const infoIcon = document.createElement('span');
    infoIcon.className = 'iframe-info-icon';
    infoIcon.textContent = 'ⓘ';
    infoIcon.setAttribute('tabindex', '0');
    infoIcon.setAttribute('aria-label', t('tarjeta.iframe.aria-info'));
    infoIcon.dataset.i18nAria = 'tarjeta.iframe.aria-info';
    const tooltip = document.createElement('div');
    tooltip.className = 'iframe-info-tooltip';
    ['tarjeta.iframe.tooltip.p1', 'tarjeta.iframe.tooltip.p2', 'tarjeta.iframe.tooltip.p3'].forEach(clave => {
        const p = document.createElement('p');
        p.dataset.i18n = clave;
        p.textContent = t(clave);
        tooltip.appendChild(p);
    });
    infoWrapper.append(infoIcon, tooltip);
    initInfoTooltip(infoIcon, tooltip);

    const btnCopiar = document.createElement('button');
    btnCopiar.className = 'vp-btn';
    btnCopiar.dataset.i18n = 'tarjeta.btn.copiar-iframe';
    btnCopiar.textContent = t('tarjeta.btn.copiar-iframe');
    btnCopiar.dataset.url = _urlEmbed();
    btnCopiar.addEventListener('click', () => {
        const codigo = `<iframe src="${btnCopiar.dataset.url}" width="100%" height="700" frameborder="0"></iframe>`;
        navigator.clipboard.writeText(codigo)
            .then(() => mostrarToast(t('tarjeta.toast.iframe.copiado'), 'exito'))
            .catch(() => mostrarToast(t('tarjeta.toast.iframe.error'), 'error'));
    });

    // — Botón "Opciones ▾" + panel desplegable
    const optsWrap = document.createElement('div');
    optsWrap.className = 'vp-opts-wrap';

    const btnOpts = document.createElement('button');
    btnOpts.className = 'vp-btn vp-btn--opts';
    btnOpts.dataset.i18n = 'tarjeta.vista-publica.opciones';
    btnOpts.innerHTML = `${t('tarjeta.vista-publica.opciones')} <span class="vp-btn--opts-arrow" aria-hidden="true">▾</span>`;

    const optsPanel = document.createElement('div');
    optsPanel.className = 'vp-opts-panel';
    optsPanel.hidden = true;

    btnOpts.addEventListener('click', () => {
        optsPanel.hidden = !optsPanel.hidden;
        btnOpts.classList.toggle('vp-btn--opts-open', !optsPanel.hidden);
    });

    function _optsToggle(labelKey, opciones, getter, setter) {
        const row = document.createElement('div');
        row.className = 'vp-opt-row';
        const lbl = document.createElement('span');
        lbl.className = 'vp-opt-label';
        lbl.dataset.i18n = labelKey;
        lbl.textContent = t(labelKey);
        const btns = document.createElement('div');
        btns.className = 'vista-publica__toggle';
        opciones.forEach(({ i18nKey, valor }) => {
            const b = document.createElement('button');
            b.className = 'vista-publica__opt' + (getter() === valor ? ' vista-publica__opt--activo' : '');
            b.dataset.i18n = i18nKey;
            b.textContent = t(i18nKey);
            b.addEventListener('click', () => {
                setter(valor);
                btns.querySelectorAll('.vista-publica__opt').forEach(x => x.classList.remove('vista-publica__opt--activo'));
                b.classList.add('vista-publica__opt--activo');
                btnVer.href = _urlPublica();
                btnCopiar.dataset.url = _urlEmbed();
            });
            btns.appendChild(b);
        });
        row.append(lbl, btns);
        return row;
    }

    optsPanel.append(
        _optsToggle(
            'tarjeta.vista-publica.idioma',
            [
                { i18nKey: 'tarjeta.vista-publica.lang.es', valor: 'es' },
                { i18nKey: 'tarjeta.vista-publica.lang.en', valor: 'en' },
            ],
            () => cardLang,
            v => { cardLang = v; localStorage.setItem(LS_LANG, v); }
        ),
        _optsToggle(
            'tarjeta.vista-publica.tema',
            [
                { i18nKey: 'tarjeta.vista-publica.tema.light', valor: 'light' },
                { i18nKey: 'tarjeta.vista-publica.tema.dark',  valor: 'dark' },
            ],
            () => cardTheme,
            v => { cardTheme = v; localStorage.setItem(LS_THEME, v); }
        )
    );

    optsWrap.append(btnOpts, optsPanel);

    const bar = document.createElement('div');
    bar.className = 'vp-bar';
    bar.append(btnVer, infoWrapper, btnCopiar, optsWrap);

    const actions = document.createElement('div');
    actions.className = 'profile-card-actions';
    actions.appendChild(bar);
    contenedor.appendChild(actions);
}

export function limpiarTarjetaResumenPerfil() {
    const contenedor = document.getElementById('dashboard-profile-card-container');
    if (!contenedor) return;
    contenedor.replaceChildren();
}